// Save-time rules for a leadRoutes doc (docs/lead-relay.md §5.2, §5.3, §9.1).
// Pure, so the route service and the CRM edit form run the SAME checks — a form
// cannot accept a route the server then refuses.
//
// ⚠️ PROVIDER SEAM (2026-10-05): this file holds the provider-NEUTRAL rules and
// dispatches the rest on the route's provider. VirtualYard's own rules (the
// pinned submit URL, the BYD interest list, the blocklist checks, the message
// template) are in virtualyard.ts.
//
// Every refusal names the field and the reason. None quotes formUrl, which
// carries the dealer's token.

import { isLeadProvider, type LeadRoute } from '../../types/leadRelay.js';
import { normalizeKey } from './normalize.js';
import { anytimeFitnessRouteProblems } from './anytimefitness.js';
import type { LeadLocale } from './locale.js';
import { PROVIDER_CAN_SEND, providerNotWiredMessage } from './providerVocabulary.js';
import {
  sharedCredentialWarnings as virtualYardCredentialWarnings,
  type VirtualYardRouteFields,
  virtualYardRouteProblems,
} from './virtualyard.js';


/** The provider-neutral editable fields of a route. */
export interface LeadRouteCommonFields {
  name: string;
  accountId: string;
  active: boolean;
  testMode: boolean;
  metaFormNames: string[];
  alertChannel: string | null;
}

/**
 * The editable shape of a route — what a create or an update supplies, and the
 * CRM's wire shape.
 *
 * ⛔ `provider` IS REQUIRED (step 3, 2026-10-06). It used to be optional with
 * "absent means virtualyard", which was right only while no picker existed:
 * once a create can be for another provider, an omitted or malformed provider
 * on the write path would SILENTLY CREATE A VIRTUALYARD ROUTE.
 *
 * ⛔ AND IT IS IMMUTABLE AFTER CREATE (updateLeadRoute refuses a change, and the
 * edit page offers no control for it). Changing it would silently reinterpret
 * every other field — `fixed.lid` is not a club id, `formUrl` means nothing to
 * another provider — and an in-flight `retrying` submission would be
 * re-attempted against a different API with fields composed for the old one.
 * There is no sensible migration, so there is no control that asks for one: a
 * different provider is a different route.
 */
export type LeadRouteFields = LeadRouteCommonFields &
  (({ provider: 'virtualyard' } & VirtualYardRouteFields) | ({ provider: 'anytimefitness' } & AnytimeFitnessRouteFields));

/**
 * Anytime Fitness's editable route fields. ⚠️ ONE club id, and the country it
 * belongs to — the per-country values are read from the country, never stored.
 */
export interface AnytimeFitnessRouteFields {
  clubId: string;
  country: LeadLocale;
}

export interface RouteProblem {
  field: string;
  message: string;
}

const ROUTE_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function isValidRouteId(id: string): boolean {
  return id.length <= 80 && ROUTE_ID.test(id);
}

function assertNever(value: never): never {
  throw new Error(`Unhandled lead provider: ${JSON.stringify(value)}`);
}

/**
 * The provider a set of fields asks for — NOT YET VALIDATED. Typed `unknown`
 * because a script or a stale client can send anything, including nothing; an
 * absent provider is refused like any other unknown one, never defaulted.
 */
export function requestedProvider(fields: Pick<LeadRouteFields, 'provider'>): unknown {
  return (fields as { provider?: unknown }).provider;
}

/**
 * Validate one route on its own. The cross-route rule (one active route per
 * account) needs the other routes — see secondActiveRouteProblem.
 *
 * ⚠️ An UNKNOWN PROVIDER IS REFUSED HERE, at save time, rather than at submit
 * time weeks later — and its provider rules are not run, since there are none.
 */
export function validateLeadRoute(
  fields: LeadRouteFields,
  /** The shared model maps by id — supply them to check a VirtualYard route's EFFECTIVE map (§25). */
  modelMapStandards?: ReadonlyMap<string, Record<string, string>>,
): RouteProblem[] {
  const problems: RouteProblem[] = [];
  const add = (field: string, message: string) => problems.push({ field, message });

  if (fields.name.trim() === '') add('name', 'is required');
  if (fields.accountId.trim() === '') add('accountId', 'is required');

  const provider = requestedProvider(fields);
  if (!isLeadProvider(provider)) {
    add('provider', `unknown provider ${JSON.stringify(provider)}`);
  } else {
    switch (fields.provider) {
      case 'virtualyard':
        problems.push(...virtualYardRouteProblems(fields, modelMapStandards));
        break;
      case 'anytimefitness':
        problems.push(...anytimeFitnessRouteProblems({ provider: 'anytimefitness', clubId: fields.clubId, country: fields.country }));
        break;
      default:
        assertNever(fields);
    }
    // ⛔ A route of a provider the relay cannot send through yet must not be
    // ACTIVE: routing would hand it leads that go nowhere. Saving it inactive is
    // fine — that is how an Anytime Fitness route is prepared before its
    // transport exists (PROVIDER_CAN_SEND).
    if (fields.active && !PROVIDER_CAN_SEND[fields.provider]) add('active', providerNotWiredMessage(fields.provider));
  }

  const seen = new Set<string>();
  for (const name of fields.metaFormNames) {
    const key = normalizeKey(name);
    if (key === '') add('metaFormNames', 'contains a blank name');
    else if (seen.has(key)) add('metaFormNames', `"${name}" is listed twice`);
    seen.add(key);
  }
  return problems;
}

/**
 * ⚠️⚠️ ONE ACTIVE ROUTE PER ACCOUNT — A HARD RULE. Leads route by account_id
 * ONLY (routing.ts), so a second active route on an account is unreachable,
 * and worse, it makes EVERY lead for that account unassigned (the relay never
 * guesses between them). So a save that would leave two active routes on one
 * account is REFUSED, naming the route already there — on the server (create,
 * update, activate) and on the edit page before the save is offered.
 *
 * Changing a destination is: deactivate the old route, then activate the new
 * one. Multiple destinations for one dealer is the post-to-many feature, not
 * multiple routes (docs/lead-relay.md).
 */
export function secondActiveRouteProblem(
  routeId: string,
  fields: Pick<LeadRouteFields, 'active' | 'accountId'>,
  others: ReadonlyArray<{ id: string; name: string; active: boolean; accountId: string }>,
): string | null {
  const accountId = fields.accountId.trim();
  if (!fields.active || accountId === '') return null;
  const clash = others.filter((o) => o.id !== routeId && o.active && o.accountId.trim() === accountId);
  if (clash.length === 0) return null;
  const names = clash.map((o) => `${o.name} (${o.id})`).join(', ');
  return `This account already has an active route: ${names}. One active route per account — deactivate that route first, then activate this one.`;
}

/**
 * Save-time warnings — things a person should look at, never refusals. They
 * compare only routes of the SAME provider (VirtualYard: a shared form token
 * rotated on some routes and not others — see virtualyard.ts).
 */
export function routeSaveWarnings(
  routeId: string,
  fields: LeadRouteFields,
  others: readonly LeadRoute[],
): string[] {
  const common: string[] = [];
  const provider = requestedProvider(fields);
  if (!isLeadProvider(provider)) return common;
  switch (fields.provider) {
    case 'virtualyard': {
      const peers = others.flatMap((other) =>
        other.config.provider === 'virtualyard'
          ? [{ id: other.id, name: other.name, active: other.active, formUrl: other.config.formUrl }]
          : [],
      );
      return [...common, ...virtualYardCredentialWarnings(routeId, fields.formUrl.trim(), peers)];
    }
    // No shared credential to drift: the AF endpoint carries no secret.
    case 'anytimefitness':
      return common;
    default:
      return assertNever(fields);
  }
}

/**
 * ⚠️⚠️ THE NEW-ROUTE LOCK — a SERVER rule, not a form default (2026-10-07).
 * A route is CREATED inactive AND in test mode; activation is a separate,
 * deliberate act afterwards. The club id / lid cannot be verified by anything
 * here (a wrong one is accepted by the provider and routes a member of the
 * public to the wrong business), so a person activating it after checking is
 * the only control there is — and a create that arrives already live skips it.
 * Run by routeService.createLeadRoute (the rule) and by the edit page for a new
 * route (so the form says so before Save). Never applied to an UPDATE.
 */
export function newRouteLockProblems(fields: Pick<LeadRouteFields, 'active' | 'testMode'>): RouteProblem[] {
  const problems: RouteProblem[] = [];
  if (fields.active) {
    problems.push({ field: 'active', message: 'A new route is created inactive. Save it first, check it, then activate it.' });
  }
  if (!fields.testMode) {
    problems.push({ field: 'testMode', message: 'A new route is created in test mode. Save it first, check it, then take it out of test mode.' });
  }
  return problems;
}
